import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAsyncHandler, type AsyncHandlerResult } from "../../../../hooks/useAsyncHandler";
import {
  usePubmedQueryValidation,
  type UsePubmedQueryValidationResult,
} from "../../../../hooks/usePubmedQueryValidation";
import { createCorpusFeed, startCorpusFeedRun } from "../../../../lib/corpusApi";
import { CORPUS_FEED_MAX_QUERY_MATCHES } from "@shared/types/corpus/api.ts";
import { useExtractionWarmup } from "../../useExtractionWarmup";
import { feedPath } from "../../../../route_paths";

/** How many matching articles the form shows as titles, enough to judge a query by. */
const SAMPLE_SIZE = 5;

export interface UseNewFeedFormResult {
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
  /** The query and its validation. Only a validated query can be saved. */
  validation: UsePubmedQueryValidationResult;
  /** Whether the extraction service that answers a validation is up. */
  extractionWarm: boolean;
  /** How many articles the validated query matches beyond what a feed may match, or null. */
  excessMatches: number | null;
  /** Whether the feed can be created: it is named and its validated query is within bounds. */
  canCreate: boolean;
  /** Creates the feed and starts its first run. */
  create: AsyncHandlerResult;
}

/**
 * The new-feed form: a name, a description and a query. The query must be validated before the
 * feed can be created, and editing it undoes that, so a feed is only created from a query whose
 * match count and sample were just seen. Creating the feed starts its first run and lands on the
 * feed. A first run that cannot start does not undo the feed: it has no next run yet, so the
 * schedule starts it at the next check.
 */
export function useNewFeedForm(): UseNewFeedFormResult {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const { warm: extractionWarm } = useExtractionWarmup();
  const validation = usePubmedQueryValidation({
    extractionWarm,
    sampleSize: SAMPLE_SIZE,
  });

  const trimmedName = name.trim();
  const matches = validation.preview?.count ?? null;
  const excessMatches =
    matches !== null && matches > CORPUS_FEED_MAX_QUERY_MATCHES ? matches : null;

  const create = useAsyncHandler(async () => {
    const feed = await createCorpusFeed({
      name: trimmedName,
      description: description.trim(),
      query: validation.trimmedQuery,
    });
    await startCorpusFeedRun(feed.id).catch(() => {});
    navigate(feedPath(feed));
  });

  return {
    name,
    setName,
    description,
    setDescription,
    validation,
    extractionWarm,
    excessMatches,
    canCreate: trimmedName !== "" && matches !== null && excessMatches === null && !create.loading,
    create,
  };
}
